import React, { useState, useEffect } from 'react';
import { Menu, X, Phone, Mail, User, LogOut, Calendar, Settings, Kanban, Users, LayoutDashboard } from 'lucide-react';
import { useAuth } from '../context/AuthContext';
import LoginModal from './LoginModal';

const Header = ({ onOpenTeams = () => {} }) => {
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [isScrolled, setIsScrolled] = useState(false);
  const [showLoginModal, setShowLoginModal] = useState(false);
  const [showUserMenu, setShowUserMenu] = useState(false);
  const { user, isAuthenticated, logout } = useAuth();
  const isAdmin = user?.role === 'admin';
  const isHR = user?.role === 'hr';
  const isClient = user?.role === 'client';
  const isUser = user?.role === 'employee';
  const isScrumMaster = user?.role === 'scrum_master';
  const isProductManager = user?.role === 'product_manager';
  const isFinance = user?.role === 'finance';
  const isSales = user?.role === 'sales';
  const isMarketing = user?.role === 'marketing';

  const getRoleLabel = (role?: string) => {
    if (!role) return "Employee";
    if (role === "admin") return "Admin";
    if (role === "hr") return "HR";
    if (role === "sales") return "Sales";
    if (role === "marketing") return "Marketing";
    if (role === "finance") return "Finance";
    if (role === "product_manager") return "Product Manager";
    if (role === "scrum_master") return "Scrum Master";
    if (role === "client") return "Client";
    return role;
  };

  const getRoleDashboardHash = (role?: string) => {
    if (role === 'admin') return '#admin';
    if (role === 'hr') return '#hr-dashboard';
    if (role === 'sales') return '#sales-dashboard';
    if (role === 'marketing') return '#marketing-dashboard';
    if (role === 'finance') return '#billing';
    if (role === 'client') return '#dashboard';
    return '#employee-dashboard';
  };

  const getRoleDashboardLabel = (role?: string) => {
    if (role === 'admin') return 'Admin Dashboard';
    if (role === 'hr') return 'HR Dashboard';
    if (role === 'sales') return 'Sales Dashboard';
    if (role === 'marketing') return 'Marketing Dashboard';
    if (role === 'finance') return 'Finance & Billing';
    if (role === 'client') return 'Client Dashboard';
    return 'Employee Dashboard';
  };

  const navigateToRoleDashboard = () => {
    window.location.hash = getRoleDashboardHash(user?.role);
    setShowUserMenu(false);
    setIsMenuOpen(false);
  };


  useEffect(() => {
    console.log("LOGGED USER:", user);
  }, [user]);

  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 50);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const navItems = [
    { label: 'Home', href: '#home' },
    { label: 'Services', href: '#services' },
    { label: 'About', href: '#about' },
    { label: 'Portfolio', href: '#portfolio' },
    { label: 'Careers', href: '#careers' },
    { label: 'Contact', href: '#contact' },
  ];

  const handlePhoneClick = () => {
    window.open('tel:+919074140432', '_self');
  };
  
  const handleEmailClick = () => {
    window.open('mailto:info@x2infotech.in?subject=Inquiry from Website&body=Hello X2InfoTech Team,%0D%0A%0D%0AI am interested in your services. Please contact me.%0D%0A%0D%0AThank you.', '_self');
  };

  const handleLogout = () => {
    logout();
    setShowUserMenu(false);
  };

  const navigateToAttendance = () => {
    window.location.hash = '#attendance';
    setShowUserMenu(false);
  };

  const navigateToProfile = () => {
    window.location.hash = '#profile';
    setShowUserMenu(false);
  };


  const navigateToBoard = () => {
    window.location.hash = '#board';
    setShowUserMenu(false);
  };

  const navigateToAdmin = () => {
    window.location.hash = '#admin';
    setShowUserMenu(false);
  };

  const navigateToJira = () => {
    window.location.hash = '#jira';
    setShowUserMenu(false);
  };

  const navigateToDashboard = () => {
    window.location.hash = '#dashboard';
    setShowUserMenu(false);
  };

  const navigateToHR = () => {
  window.location.hash = '#hr-dashboard';
  setShowUserMenu(false);
};

  const navigateToTeams = () => {
  window.location.hash = '#teams';
  setShowUserMenu(false);
};


  return (
    <>
      <header className={`fixed w-full top-0 z-50 pointer-events-none transition-all duration-300 ${isScrolled ? 'bg-white/95 backdrop-blur-md shadow-lg' : 'bg-white shadow-lg'
        }`}>
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pointer-events-auto">
          <div className="flex justify-between items-center py-4">
            {/* Logo */}
            <div className="flex items-center transform hover:scale-105 transition-transform duration-200">
              <img
                src="/logofull.webp"
                alt="X2InfoTech Logo"
                className="h-12 w-auto"
              />
            </div>

            {/* Desktop Navigation */}
            <nav className="hidden md:flex space-x-8">
              {navItems.map((item, index) => (
                <a
                  key={item.label}
                  href={item.href}
                  className="text-gray-700 hover:text-brand-primary px-3 py-2 rounded-md text-sm font-medium transition-all duration-200 relative group transform hover:-translate-y-0.5"
                  style={{ animationDelay: `${index * 0.1}s` }}
                >
                  {item.label}
                  <span className="absolute bottom-0 left-0 w-0 h-0.5 bg-brand-primary transition-all duration-300 group-hover:w-full"></span>
                </a>
              ))}
            </nav>

            {/* Right Side - Contact Info & User */}
            <div className="hidden lg:flex items-center space-x-4">
              <button
                onClick={handlePhoneClick}
                className="flex items-center text-sm text-gray-600 hover:text-brand-primary transition-all duration-200 transform hover:scale-105 hover:-translate-y-0.5 group"
              >
                <Phone className="h-4 w-4 mr-1 text-brand-primary group-hover:animate-pulse" />
                <span>+91 90741 40432</span>
              </button>
              <button
                onClick={handleEmailClick}
                className="flex items-center text-sm text-gray-600 hover:text-brand-primary transition-all duration-200 transform hover:scale-105 hover:-translate-y-0.5 group"
              >
                <Mail className="h-4 w-4 mr-1 text-brand-primary group-hover:animate-bounce" />
                <span>info@x2infotech.in</span>
              </button>

              {/* User Menu */}
              {isAuthenticated && user ? (
                <div className="relative flex items-center space-x-2">
                  <button
                    onClick={navigateToRoleDashboard}
                    className="hidden sm:flex items-center space-x-1.5 bg-emerald-600 hover:bg-emerald-700 text-white text-xs font-bold px-3 py-2 rounded-lg shadow-sm transition-all cursor-pointer"
                  >
                    <LayoutDashboard className="h-3.5 w-3.5" />
                    <span>{getRoleDashboardLabel(user.role)}</span>
                  </button>

                  <button
                    onClick={() => setShowUserMenu(!showUserMenu)}
                    className="flex items-center space-x-2 bg-slate-100 hover:bg-slate-200 text-slate-800 border border-slate-300 px-3 py-2 rounded-lg transition-all duration-200 cursor-pointer"
                  >
                    <User className="h-4 w-4 text-emerald-600" />
                    <span className="text-sm font-semibold">{getRoleLabel(user.role)}</span>
                  </button>

                  {showUserMenu && (
                    <div className="absolute right-0 top-12 w-56 bg-white rounded-xl shadow-xl border border-slate-200 py-2 animate-fade-in z-50">
                      <div className="px-4 py-2.5 border-b border-slate-100">
                        <p className="text-sm font-bold text-slate-900 truncate">
                          {user.name}
                        </p>
                        <p className="text-xs text-slate-500 truncate">{user.email}</p>
                        <p className="text-[11px] font-bold text-emerald-700 uppercase tracking-wider mt-0.5">
                          Role: {getRoleLabel(user.role)}
                        </p>
                      </div>

                      {/* Tailored Primary Dashboard */}
                      <button
                        onClick={navigateToRoleDashboard}
                        className="w-full text-left px-4 py-2.5 text-xs font-bold text-emerald-800 bg-emerald-50 hover:bg-emerald-100 flex items-center transition-colors cursor-pointer border-b border-emerald-100"
                      >
                        <LayoutDashboard className="h-4 w-4 mr-2 text-emerald-600" />
                        {getRoleDashboardLabel(user.role)}
                      </button>

                      <button
                        onClick={navigateToProfile}
                        className="w-full text-left px-4 py-2 text-xs text-slate-700 hover:bg-slate-50 flex items-center transition-colors cursor-pointer"
                      >
                        <User className="h-4 w-4 mr-2 text-slate-400" />
                        My Profile
                      </button>

                      {/* Attendance: for internal staff only, not client */}
                      {!isClient && (
                        <button
                          onClick={navigateToAttendance}
                          className="w-full text-left px-4 py-2 text-xs text-slate-700 hover:bg-slate-50 flex items-center transition-colors cursor-pointer"
                        >
                          <Calendar className="h-4 w-4 mr-2 text-slate-400" />
                          Attendance Tracker
                        </button>
                      )}

                      {/* Jira board for dev/pm/scrum/admin */}
                      {(isAdmin || isUser || isProductManager || isScrumMaster) && (
                        <button
                          onClick={navigateToBoard}
                          className="w-full text-left px-4 py-2 text-xs text-slate-700 hover:bg-slate-50 flex items-center transition-colors cursor-pointer"
                        >
                          <Kanban className="h-4 w-4 mr-2 text-slate-400" />
                          Sprint Board
                        </button>
                      )}

                      <button
                        onClick={navigateToTeams}
                        className="w-full text-left px-4 py-2 text-xs text-slate-700 hover:bg-slate-50 flex items-center transition-colors cursor-pointer"
                      >
                        <Users className="h-4 w-4 mr-2 text-slate-400" />
                        Teams Chat
                      </button>

                      <div className="border-t border-slate-100 mt-1 pt-1">
                        <button
                          onClick={handleLogout}
                          className="w-full text-left px-4 py-2 text-xs text-red-600 hover:bg-red-50 flex items-center transition-colors cursor-pointer font-semibold"
                        >
                          <LogOut className="h-4 w-4 mr-2 text-red-500" />
                          Logout
                        </button>
                      </div>
                    </div>
                  )}
                </div>
              ) : (
                <button
                  onClick={() => setShowLoginModal(true)}
                  className="bg-gradient-to-r from-[#10b981] via-[#14c9cb] to-[#08b3e5] text-white px-5 py-2.5 rounded-xl font-semibold shadow-sm hover:shadow-[0_4px_18px_rgba(20,201,203,0.35)] transition-all duration-200 transform hover:scale-105 flex items-center"
                >
                  <User className="h-4 w-4 mr-2" />
                  Login
                </button>
              )}
            </div>

            {/* Mobile menu button */}
            <div className="md:hidden flex items-center space-x-2">
              {isAuthenticated && user && (
                <button
                  onClick={() => setShowUserMenu(!showUserMenu)}
                  className="bg-brand-primary text-white p-2 rounded-lg"
                >
                  <User className="h-4 w-4" />
                </button>
              )}
              <button
                onClick={() => setIsMenuOpen(!isMenuOpen)}
                className="text-gray-700 hover:text-brand-primary focus:outline-none focus:text-brand-primary transform hover:scale-110 transition-all duration-200"
              >
                {isMenuOpen ? <X className="h-6 w-6" /> : <Menu className="h-6 w-6" />}
              </button>
            </div>
          </div>

          {/* Mobile Navigation */}
          {isMenuOpen && (
            <div className="md:hidden animate-fade-in-down">
              <div className="px-2 pt-2 pb-3 space-y-1 sm:px-3 bg-white/95 backdrop-blur-md border-t">
                {navItems.map((item, index) => (
                  <a
                    key={item.label}
                    href={item.href}
                    className="text-gray-700 hover:text-brand-primary block px-3 py-2 rounded-md text-base font-medium transition-all duration-200 transform hover:translate-x-2"
                    onClick={() => setIsMenuOpen(false)}
                    style={{ animationDelay: `${index * 0.1}s` }}
                  >
                    {item.label}
                  </a>
                ))}
                <div className="flex flex-col space-y-2 px-3 py-2">
                  <button
                    onClick={handlePhoneClick}
                    className="flex items-center text-sm text-gray-600 hover:text-brand-primary transition-colors duration-200"
                  >
                    <Phone className="h-4 w-4 mr-2 text-brand-primary" />
                    <span>+91 90741 40432</span>
                  </button>
                  <button
                    onClick={handleEmailClick}
                    className="flex items-center text-sm text-gray-600 hover:text-brand-primary transition-colors duration-200"
                  >
                    <Mail className="h-4 w-4 mr-2 text-brand-primary" />
                    <span>info@x2infotech.in</span>
                  </button>
                  {!isAuthenticated && (
                    <button
                      onClick={() => {
                        setShowLoginModal(true);
                        setIsMenuOpen(false);
                      }}
                      className="bg-brand-primary text-white px-4 py-2 rounded-lg hover:bg-brand-dark transition-all duration-200 flex items-center justify-center mt-2"
                    >
                      <User className="h-4 w-4 mr-2" />
                      Login
                    </button>
                  )}
                </div>
              </div>
            </div>
          )}



          {/* Mobile User Menu */}
          {showUserMenu && isAuthenticated && user && (
            <div className="md:hidden bg-white border-t border-slate-200 py-3 px-4 space-y-2 animate-fade-in">
              <div className="pb-2 border-b border-slate-100">
                <p className="text-sm font-bold text-slate-900">{user.name}</p>
                <p className="text-xs text-slate-500">{user.email}</p>
                <p className="text-xs font-bold text-emerald-700 uppercase mt-0.5">
                  Role: {getRoleLabel(user.role)}
                </p>
              </div>

              {/* Primary Role Dashboard */}
              <button
                onClick={navigateToRoleDashboard}
                className="w-full text-left px-3 py-2 text-sm font-bold text-emerald-800 bg-emerald-50 rounded-lg flex items-center"
              >
                <LayoutDashboard className="h-4 w-4 mr-2 text-emerald-600" />
                {getRoleDashboardLabel(user.role)}
              </button>

              <button
                onClick={navigateToProfile}
                className="w-full text-left px-3 py-2 text-sm text-slate-700 hover:bg-slate-50 rounded-lg flex items-center"
              >
                <User className="h-4 w-4 mr-2 text-slate-400" />
                My Profile
              </button>

              {!isClient && (
                <button
                  onClick={navigateToAttendance}
                  className="w-full text-left px-3 py-2 text-sm text-slate-700 hover:bg-slate-50 rounded-lg flex items-center"
                >
                  <Calendar className="h-4 w-4 mr-2 text-slate-400" />
                  Attendance Tracker
                </button>
              )}

              {(isAdmin || isUser || isProductManager || isScrumMaster) && (
                <button
                  onClick={navigateToBoard}
                  className="w-full text-left px-3 py-2 text-sm text-slate-700 hover:bg-slate-50 rounded-lg flex items-center"
                >
                  <Kanban className="h-4 w-4 mr-2 text-slate-400" />
                  Sprint Board
                </button>
              )}

              <button
                onClick={navigateToTeams}
                className="w-full text-left px-3 py-2 text-sm text-slate-700 hover:bg-slate-50 rounded-lg flex items-center"
              >
                <Users className="h-4 w-4 mr-2 text-slate-400" />
                Teams Chat
              </button>

              <div className="pt-2 border-t border-slate-100">
                <button
                  onClick={handleLogout}
                  className="w-full text-left px-3 py-2 text-sm font-semibold text-red-600 hover:bg-red-50 rounded-lg flex items-center"
                >
                  <LogOut className="h-4 w-4 mr-2 text-red-500" />
                  Logout
                </button>
              </div>
            </div>
          )}
        </div>
      </header>

      {/* Login Modal */}
      <LoginModal
        isOpen={showLoginModal}
        onClose={() => setShowLoginModal(false)}
      />
    </>
  );
};

export default Header;